import { Home, Wind, ShieldCheck, Sprout } from "lucide-react";

const points = [
  {
    icon: Home,
    title: "Around 18,000 Irish homes powered every year",
    body: "76 MWp of clean electricity, generated quietly in the Galway countryside, feeds straight into the national grid — every year, for decades.",
  },
  {
    icon: Wind,
    title: "Roughly 21,000 tonnes of CO₂ avoided annually",
    body: "That's the equivalent of taking around 4,500 diesel cars off Irish roads — every single year. Cleaner air for the region, and a real contribution to Ireland's 2030 climate targets.",
  },
  {
    icon: ShieldCheck,
    title: "Less reliance on imported fossil fuels",
    body: "Home‑grown renewable generation insulates Irish households and businesses from global gas price shocks, and helps meet the country's commitment to 80% renewable electricity by 2030.",
  },
  {
    icon: Sprout,
    title: "Better land outcomes, not worse",
    body: "Reduced stocking helps soils recover. Mature hedgerows are kept and bolstered, an Annex I Molinia meadow is protected, and a Biodiversity Enhancement Management Plan creates new habitat.",
  },
];

export default function WhySection() {
  return (
    <section className="bg-[#F9F8F6]" data-testid="why-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-12">
          <div className="lg:col-span-5 reveal">
            <p className="overline mb-4">Why this project</p>
            <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight">
              Why building this solar farm matters.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 reveal">
            <p className="text-[15px] leading-relaxed text-[#2A2B2A]/85">
              The numbers above tell a clear story. Solar projects like Loughrea
              are one of the fastest, cleanest and most reversible ways for
              Ireland to hit its climate and energy security targets — and to
              build local resilience along the way.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E0DCD3] border border-[#E0DCD3] rounded-sm overflow-hidden">
          {points.map((p, i) => {
            const Ic = p.icon;
            return (
              <div key={i} className="bg-white p-8 lg:p-10" data-testid={`why-point-${i}`}>
                <Ic className="h-5 w-5 text-[#3A4B3A]" strokeWidth={1.8} />
                <h3 className="mt-5 font-serif-display text-xl tracking-tight">{p.title}</h3>
                <p className="mt-3 text-[14.5px] leading-relaxed text-[#63635E]">{p.body}</p>
              </div>
            );
          })}
        </div>

        {/* Visual comparison strip */}
        <div className="mt-12 rounded-sm border border-[#E0DCD3] bg-white overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#E0DCD3]">
            <ComparisonTile
              big="4,500"
              unit="diesel cars"
              caption="Equivalent annual emissions removed from Irish roads"
              icon="car"
            />
            <ComparisonTile
              big="21,000 t"
              unit="CO₂ / year"
              caption="Avoided greenhouse gas emissions, displacing grid generation"
              icon="cloud"
            />
            <ComparisonTile
              big="76 GWh"
              unit="clean electricity / year"
              caption="Enough for ~18,000 Irish homes' annual usage"
              icon="bolt"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function ComparisonTile({ big, unit, caption, icon }) {
  return (
    <div className="p-8 lg:p-10 flex items-start gap-5">
      <div className="shrink-0 mt-1">
        <ComparisonIcon icon={icon} />
      </div>
      <div>
        <div className="font-serif-display text-3xl lg:text-4xl tracking-tight text-[#3A4B3A]">
          {big}
        </div>
        <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#CBA365] mt-2">
          {unit}
        </div>
        <p className="mt-3 text-[13.5px] leading-relaxed text-[#63635E]">
          {caption}
        </p>
      </div>
    </div>
  );
}

function ComparisonIcon({ icon }) {
  // Hand-drawn-feeling SVG glyphs matching the rest of the diagrams.
  if (icon === "car") {
    return (
      <svg width="46" height="46" viewBox="0 0 46 46" fill="none" aria-hidden="true">
        <rect x="1" y="1" width="44" height="44" rx="2" fill="#F2EFE8" stroke="#E0DCD3" />
        <path d="M9 27 L12 19 Q13 17 15 17 H31 Q33 17 34 19 L37 27 V31 H9 Z" fill="#3A4B3A" />
        <rect x="14" y="20" width="8" height="5" rx="1" fill="#8C9B86" />
        <rect x="24" y="20" width="8" height="5" rx="1" fill="#8C9B86" />
        <circle cx="15" cy="31" r="3" fill="#2A2B2A" />
        <circle cx="31" cy="31" r="3" fill="#2A2B2A" />
      </svg>
    );
  }
  if (icon === "cloud") {
    return (
      <svg width="46" height="46" viewBox="0 0 46 46" fill="none" aria-hidden="true">
        <rect x="1" y="1" width="44" height="44" rx="2" fill="#F2EFE8" stroke="#E0DCD3" />
        <path d="M11 28 Q9 22 15 21 Q16 16 22 16 Q28 16 29 21 Q35 21 35 26 Q35 30 30 30 H14 Q11 30 11 28 Z" fill="#3A4B3A" />
        <path d="M16 34 L14 38 M22 34 L20 38 M28 34 L26 38" stroke="#3A4B3A" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  }
  // bolt
  return (
    <svg width="46" height="46" viewBox="0 0 46 46" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="44" height="44" rx="2" fill="#F2EFE8" stroke="#E0DCD3" />
      <path d="M25 8 L13 26 H22 L19 38 L33 18 H24 Z" fill="#CBA365" stroke="#3A4B3A" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
}
